import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBot } from "@/context/BotContext";
import {
  LayoutDashboard, Settings, History, BarChart3, FlaskConical,
  LogOut, Menu, X, Plus, Bot, Trash2, Circle, ChevronDown, Pencil,
} from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/history", label: "History", icon: History },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/backtest", label: "Backtest", icon: FlaskConical },
  { path: "/settings", label: "Settings", icon: Settings },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { bots, selectedBot, selectBot, createBot, updateBot, deleteBot } = useBot();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showNewBot, setShowNewBot] = useState(false);
  const [newBotName, setNewBotName] = useState("");
  const [newBotMode, setNewBotMode] = useState("paper");
  const [newBotWindow, setNewBotWindow] = useState(5);  // 5, 15, 60, 1440
  const [creating, setCreating] = useState(false);
  const [botListOpen, setBotListOpen] = useState(true);
  const [renamingBotId, setRenamingBotId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const startRename = (e, bot) => {
    e.stopPropagation();
    setRenamingBotId(bot.id);
    setRenameValue(bot.name);
  };

  const cancelRename = () => {
    setRenamingBotId(null);
    setRenameValue("");
  };

  const commitRename = async (botId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { cancelRename(); return; }
    const original = bots.find(b => b.id === botId);
    if (original && trimmed === original.name) { cancelRename(); return; }
    setSavingRename(true);
    try {
      await updateBot(botId, { name: trimmed });
    } catch (err) {
      console.error("Rename failed:", err);
    } finally {
      setSavingRename(false);
      cancelRename();
    }
  };

  const handleCreateBot = async () => {
    if (!newBotName.trim()) return;
    setCreating(true);
    try {
      const payload = {
        name: newBotName.trim(),
        mode: newBotMode,
        window_minutes: newBotWindow,
      };
      await createBot(payload);
      setNewBotName("");
      setNewBotMode("paper");
      setNewBotWindow(5);
      setShowNewBot(false);
    } catch (err) {
      console.error("Create bot failed:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBot = async (e, botId, botName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${botName}" and all its trades?`)) return;
    try { await deleteBot(botId); } catch (err) { console.error(err); }
  };

  const modeTag = (m) => m === "live"
    ? { bg: "rgba(0,229,168,0.1)", color: "#00E5A8", border: "rgba(0,229,168,0.2)", label: "LIVE" }
    : { bg: "rgba(0,194,255,0.1)", color: "#00C2FF", border: "rgba(0,194,255,0.2)", label: "PAPER" };

  const statusDot = (bot) => bot.is_running ? "#22c55e" : "#3a4555";

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>

      {/* ─── SIDEBAR (Desktop) ─── */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-[232px] flex-col z-40"
        style={{ background: 'var(--bg-card)', borderRight: '1px solid var(--border)' }}>

        <Link to="/" className="flex items-center gap-3 px-5 py-5">
          <img src="/logo.png" alt="Edge Bot" className="w-8 h-8 rounded-lg" />
          <div>
            <span className="text-base font-bold tracking-tight block" style={{ color: 'var(--text-primary)' }}>Edge Bot</span>
            <span className="text-[9px] font-medium uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
              by Probability Lab
            </span>
          </div>
        </Link>

        {/* Bot List — moved to top bar */}

        {/* Nav */}
        <nav className="flex-1 px-3 py-3 space-y-0.5">
          {navItems.map(({ path, label, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <Link key={path} to={path} data-testid={`nav-${label.toLowerCase()}`}
                className={`sidebar-nav-item relative ${active ? "active" : ""}`}>
                <Icon style={{ width: 17, height: 17, strokeWidth: 1.8 }} />
                {label}
              </Link>
            );
          })}
        </nav>

        <button data-testid="logout-btn" onClick={handleLogout}
          className="flex items-center gap-2 px-5 py-4 text-sm transition-colors"
          style={{ borderTop: '1px solid var(--border)', color: 'var(--text-secondary)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-red)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}>
          <LogOut style={{ width: 17, height: 17, strokeWidth: 1.8 }} />
          Logout
        </button>
      </aside>

      {/* ─── MOBILE TOP BAR ─── */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 z-50 flex items-center justify-between px-4"
        style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo.png" alt="Edge Bot" className="w-7 h-7 rounded-lg" />
          <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Edge Bot</span>
        </Link>
        <div className="flex items-center gap-2">
          {selectedBot && (() => {
            const mt = modeTag(selectedBot.mode);
            return (
              <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-[2px] rounded"
                style={{ background: mt.bg, color: mt.color, border: `1px solid ${mt.border}` }}>
                {selectedBot.name.slice(0, 14)}
              </span>
            );
          })()}
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: 'var(--text-secondary)' }}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="absolute top-14 left-0 right-0 z-50"
            style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
            {/* Bot list */}
            <div className="px-4 py-2 space-y-1" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="text-[9px] font-semibold uppercase tracking-widest py-1" style={{ color: 'var(--text-secondary)' }}>Switch Bot</div>
              {bots.map(bot => {
                const active = bot.id === selectedBot?.id;
                const mt = modeTag(bot.mode);
                return (
                  <button key={bot.id}
                    onClick={() => { if (renamingBotId !== bot.id) { selectBot(bot.id); setMobileOpen(false); } }}
                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left"
                    style={{ background: active ? 'rgba(0,212,255,0.06)' : 'transparent' }}>
                    <Circle fill={statusDot(bot)} stroke="none" className="w-2 h-2" />
                    {renamingBotId === bot.id ? (
                      <input
                        data-testid={`rename-bot-input-mobile-${bot.id}`}
                        autoFocus
                        value={renameValue}
                        disabled={savingRename}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); commitRename(bot.id); }
                          if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                        }}
                        onBlur={() => commitRename(bot.id)}
                        maxLength={32}
                        className="text-xs font-medium flex-1 bg-transparent outline-none px-1 rounded"
                        style={{ color: 'var(--text-primary)', border: '1px solid var(--accent-cyan)' }}
                      />
                    ) : (
                      <span className="text-xs font-medium flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{bot.name}</span>
                    )}
                    <span className="text-[8px] font-bold uppercase px-1.5 py-[1px] rounded"
                      style={{ background: mt.bg, color: mt.color, border: `1px solid ${mt.border}` }}>{mt.label}</span>
                    {renamingBotId !== bot.id && (
                      <Pencil
                        data-testid={`rename-bot-btn-mobile-${bot.id}`}
                        className="w-3 h-3 opacity-60 shrink-0 cursor-pointer"
                        style={{ color: 'var(--accent-cyan)' }}
                        onClick={(e) => startRename(e, bot)} />
                    )}
                  </button>
                );
              })}
              <button onClick={() => { setMobileOpen(false); setShowNewBot(true); }}
                className="w-full flex items-center gap-2 px-2 py-2 text-xs font-semibold"
                style={{ color: 'var(--accent-cyan)' }}>
                <Plus className="w-3 h-3" />Add Bot
              </button>
            </div>
            {/* Nav */}
            <nav className="py-1">
              {navItems.map(({ path, label, icon: Icon }) => {
                const active = location.pathname === path;
                return (
                  <Link key={path} to={path} onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm transition-colors"
                    style={{ color: active ? 'var(--accent-teal)' : 'var(--text-secondary)' }}>
                    <Icon style={{ width: 17, height: 17, strokeWidth: 1.8 }} />{label}
                  </Link>
                );
              })}
              <button onClick={handleLogout}
                className="flex items-center gap-3 px-5 py-3 text-sm w-full"
                style={{ color: 'var(--accent-red)', borderTop: '1px solid var(--border)' }}>
                <LogOut style={{ width: 17, height: 17, strokeWidth: 1.8 }} />Logout
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* ─── BOTTOM NAV (Mobile) ─── */}
      <nav className="md:hidden bottom-nav">
        <div className="flex justify-around">
          {navItems.map(({ path, label, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <Link key={path} to={path} data-testid={`bnav-${label.toLowerCase()}`}
                className="flex flex-col items-center gap-0.5 py-1 px-2 transition-colors"
                style={{ color: active ? 'var(--accent-teal)' : 'var(--text-secondary)' }}>
                <Icon style={{ width: 18, height: 18, strokeWidth: 1.8 }} />
                <span className="text-[9px] font-semibold uppercase tracking-wider">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* ─── NEW BOT MODAL ─── */}
      {showNewBot && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setShowNewBot(false)}>
          <div className="w-full max-w-sm rounded-xl overflow-hidden"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center gap-2.5 px-5 pt-5 pb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,194,255,0.08)', border: '1px solid rgba(0,194,255,0.15)' }}>
                <Bot className="w-4 h-4" style={{ color: 'var(--accent-cyan)' }} />
              </div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>New Bot</h3>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block"
                  style={{ color: 'var(--text-secondary)' }}>Name</label>
                <input data-testid="new-bot-name-input"
                  value={newBotName} onChange={e => setNewBotName(e.target.value)}
                  placeholder="e.g. Aggressive $10"
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(0,194,255,0.4)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && newBotName.trim()) handleCreateBot(); }}
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block"
                  style={{ color: 'var(--text-secondary)' }}>Mode</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "paper", label: "Paper", desc: "Simulate trades", color: "#00C2FF" },
                    { value: "live", label: "Live", desc: "Real money", color: "#00E5A8" },
                  ].map(opt => (
                    <button key={opt.value} data-testid={`new-bot-mode-${opt.value}`}
                      onClick={() => setNewBotMode(opt.value)}
                      className="flex flex-col items-center gap-1 py-3 rounded-lg transition-all"
                      style={{
                        background: newBotMode === opt.value ? `${opt.color}08` : 'var(--bg-primary)',
                        border: `1px solid ${newBotMode === opt.value ? `${opt.color}30` : 'var(--border)'}`,
                      }}>
                      <span className="text-xs font-bold" style={{ color: newBotMode === opt.value ? opt.color : 'var(--text-secondary)' }}>
                        {opt.label}
                      </span>
                      <span className="text-[9px]" style={{ color: 'var(--text-secondary)' }}>{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {newBotMode === "live" && (
                <p className="text-[10px] px-2 py-1.5 rounded-md" style={{ color: 'var(--accent-yellow)', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                  Configure your Polymarket wallet in Settings after creating the bot.
                </p>
              )}

              <div data-testid="new-bot-strategy-info" className="text-[10px] rounded-lg p-2.5 leading-relaxed"
                style={{ background: 'rgba(0,229,168,0.06)', border: '1px solid rgba(0,229,168,0.20)', color: 'var(--text-secondary)' }}>
                <div className="text-xs font-bold mb-1" style={{ color: '#00E5A8' }}>Green-Fade Bot</div>
                After a new <b style={{color:'#E6EDF3'}}>green candle</b> it bets <b style={{color:'#E6EDF3'}}>DOWN $10</b> on the next market. If that loses (green again) it bets <b style={{color:'#E6EDF3'}}>UP $20</b> on the next one. You only lose both on green → green → red. Then it waits for a new green candle. Edit amounts in Settings.
              </div>

              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 block"
                  style={{ color: 'var(--text-secondary)' }}>Market Window</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 5, label: "5-min BTC", desc: "Fast cycle", color: "#F59E0B" },
                    { value: 15, label: "15-min BTC", desc: "Standard cycle", color: "#00C2FF" },
                    { value: 60, label: "1-hour BTC", desc: "Slow cycle", color: "#00E5A8" },
                    { value: 1440, label: "Daily BTC", desc: "1-per-day cycle", color: "#EF4444" },
                  ].map(opt => (
                    <button key={opt.value} data-testid={`new-bot-window-${opt.value}`}
                      onClick={() => setNewBotWindow(opt.value)}
                      className="flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-lg transition-all text-left"
                      style={{
                        background: newBotWindow === opt.value ? `${opt.color}10` : 'var(--bg-primary)',
                        border: `1px solid ${newBotWindow === opt.value ? `${opt.color}40` : 'var(--border)'}`,
                      }}>
                      <span className="text-xs font-bold" style={{ color: newBotWindow === opt.value ? opt.color : 'var(--text-primary)' }}>
                        {opt.label}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

            </div>

            <div className="flex gap-2 px-5 pb-5">
              <button onClick={() => setShowNewBot(false)}
                className="flex-1 py-2.5 rounded-lg text-xs font-semibold transition-colors"
                style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>Cancel</button>
              <button data-testid="create-bot-confirm-btn"
                onClick={handleCreateBot} disabled={creating || !newBotName.trim()}
                className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-opacity disabled:opacity-30"
                style={{ background: 'var(--accent-cyan)', color: '#0B0F1A' }}>
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TOP BOT SELECTOR BAR ─── */}
      <div className="hidden md:block fixed top-0 left-[232px] right-0 z-30"
        style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        {/* Edge fade to hint at horizontal scroll */}
        <div className="pointer-events-none absolute top-0 right-0 h-full w-12 z-10"
          style={{ background: 'linear-gradient(to left, var(--bg-card), transparent)' }} />
        <div className="flex items-center gap-1.5 px-4 py-2 overflow-x-auto scrollbar-hide">
          {bots.map(bot => {
            const active = bot.id === selectedBot?.id;
            const mt = modeTag(bot.mode);
            return (
              <button key={bot.id} data-testid={`bot-tab-${bot.id}`}
                onClick={() => { if (renamingBotId !== bot.id) selectBot(bot.id); }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-all shrink-0 group whitespace-nowrap"
                style={{
                  background: active ? 'rgba(0,212,255,0.08)' : 'var(--bg-primary)',
                  border: `1px solid ${active ? 'rgba(0,212,255,0.25)' : 'var(--border)'}`,
                  minWidth: 'fit-content',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'var(--bg-primary)'; }}
              >
                <Circle fill={statusDot(bot)} stroke="none" className="w-2 h-2 shrink-0" />
                {renamingBotId === bot.id ? (
                  <input
                    data-testid={`rename-bot-input-${bot.id}`}
                    autoFocus
                    value={renameValue}
                    disabled={savingRename}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); commitRename(bot.id); }
                      if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                    }}
                    onBlur={() => commitRename(bot.id)}
                    maxLength={32}
                    className="text-[11px] font-semibold bg-transparent outline-none px-1 rounded"
                    style={{
                      color: 'var(--text-primary)',
                      border: '1px solid var(--accent-cyan)',
                      width: `${Math.max(80, renameValue.length * 7 + 16)}px`,
                      maxWidth: '200px',
                    }}
                  />
                ) : (
                  <span className="text-[11px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{
                      color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                      maxWidth: '140px',
                    }}
                    title={bot.name}
                  >
                    {bot.name}
                  </span>
                )}
                {bot.tf_phase && bot.tf_phase !== "idle" && (
                  <span data-testid={`bot-phase-badge-${bot.id}`} className="text-[8px] font-bold uppercase tracking-wider px-1 py-[1px] rounded shrink-0"
                    style={{ background: 'rgba(245,158,11,0.10)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.25)' }}
                    title={bot.tf_phase === "bet1" ? "Next bet: bet 1 DOWN" : "Next bet: bet 2 UP"}>
                    {bot.tf_phase === "bet1" ? "B1" : "B2"}
                  </span>
                )}
                <span data-testid={`bot-window-badge-${bot.id}`}
                  className="text-[8px] font-bold uppercase tracking-wider px-1 py-[1px] rounded shrink-0"
                  style={(() => {
                    const wm = bot.window_minutes || 5;
                    const cfg = wm === 5 ? { c: '#F59E0B', l: '5m' }
                              : wm === 15 ? { c: '#00C2FF', l: '15m' }
                              : wm === 60 ? { c: '#00E5A8', l: '1H' }
                              : wm === 1440 ? { c: '#EF4444', l: '1D' }
                              : { c: '#6B7685', l: `${wm}m` };
                    return {
                      background: `${cfg.c}1a`,
                      color: cfg.c,
                      border: `1px solid ${cfg.c}40`,
                    };
                  })()}
                  title={`Trading window: ${bot.window_minutes || 5} minutes`}>
                  {(() => {
                    const wm = bot.window_minutes || 5;
                    return wm === 60 ? '1H' : wm === 1440 ? '1D' : `${wm}m`;
                  })()}
                </span>
                <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded shrink-0"
                  style={{ background: mt.bg, color: mt.color, border: `1px solid ${mt.border}` }}>
                  {mt.label}
                </span>
                {renamingBotId !== bot.id && (
                  <Pencil
                    data-testid={`rename-bot-btn-${bot.id}`}
                    className="w-3 h-3 opacity-30 md:opacity-0 group-hover:opacity-60 transition-opacity shrink-0 cursor-pointer hover:!opacity-100"
                    style={{ color: 'var(--accent-cyan)' }}
                    onClick={(e) => startRename(e, bot)} />
                )}
                {bots.length > 1 && renamingBotId !== bot.id && (
                  <Trash2 className="w-3 h-3 opacity-30 md:opacity-0 group-hover:opacity-60 transition-opacity shrink-0 cursor-pointer hover:!opacity-100"
                    style={{ color: 'var(--accent-red)' }}
                    onClick={(e) => handleDeleteBot(e, bot.id, bot.name)} />
                )}
              </button>
            );
          })}
          <button data-testid="add-bot-btn"
            onClick={() => setShowNewBot(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all shrink-0 mr-12"
            style={{ color: 'var(--text-secondary)', border: '1px dashed var(--border)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-cyan)'; e.currentTarget.style.borderColor = 'rgba(0,194,255,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <Plus className="w-3 h-3" />Add Bot
          </button>
        </div>
      </div>

      {/* ─── MAIN CONTENT ─── */}
      <main className="md:ml-[232px] pt-14 md:pt-12 pb-20 md:pb-0 min-h-screen relative z-10">
        <div className="p-4 md:p-6 max-w-[1240px] mx-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
